import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, List, ListItem, ListItemText, Alert, CircularProgress,
} from '@mui/material';
import type { Study } from '../../api/types';
import { api } from '../../api/rpc';

interface AutoAddOrderDialogProps {
  open: boolean;
  studies: Study[];
  onClose: () => void;
  onComplete: () => void;
}

export const AutoAddOrderDialog = ({ open, studies, onClose, onComplete }: AutoAddOrderDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      await api.autoAddOrders(studies.map(s => s.objKey));
      onComplete();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to auto-add orders');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Auto Add Orders</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Alert severity="info" sx={{ mb: 2 }}>
          Auto-add orders for {studies.length} {studies.length === 1 ? 'study' : 'studies'}?
        </Alert>
        <List dense>
          {studies.slice(0, 10).map(s => (
            <ListItem key={s.objKey}>
              <ListItemText
                primary={s.name}
                secondary={`${s.acc || ''} — ${s.descr || s.mod || ''}`}
              />
            </ListItem>
          ))}
          {studies.length > 10 && (
            <ListItem>
              <ListItemText primary={`... and ${studies.length - 10} more`} />
            </ListItem>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Auto Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
